import { ADSProvider } from '@ads/components-react';
import type { ReactNode } from 'react';

import { I18nProvider } from './I18nProvider';
import { JotaiProvider } from './JotaiProvider';
import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';

import '@ads/components-react/dist/Mbx-styles.css';

interface AppProvidersProps {
  readonly children: ReactNode;
}

/**
 * Composição ordenada de todos os providers globais da aplicação.
 *
 * A ordem de composição é crítica — cada provider pode depender dos acima dele:
 *
 * 1. **JotaiProvider**: estado atômico global disponível para todos os providers
 * 2. **QueryProvider**: TanStack Query — pode ler átomos Jotai em configurações
 * 3. **I18nProvider**: tradução disponível para todos os componentes abaixo
 * 4. **ThemeProvider**: tema — pode usar estado Jotai e traduções
 *
 * Adicione novos providers globais aqui, documentando a posição e o motivo.
 *
 * @example
 * ```tsx
 * // src/app/main.tsx
 * createRoot(document.getElementById('root')!).render(
 *   <AppProviders>
 *     <RouterProvider router={router} />
 *   </AppProviders>
 * );
 * ```
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <JotaiProvider>
      {/* JotaiProvider primeiro: estado atômico disponível para QueryProvider */}
      <QueryProvider>
        {/* QueryProvider: cliente de dados — não depende de i18n ou tema */}
        <I18nProvider>
          {/* I18nProvider: traduções disponíveis para ThemeProvider e UI */}
          <ThemeProvider>
            <ADSProvider>
              {/* ThemeProvider: aplica classes CSS de tema na raiz do DOM */}
              {children}
            </ADSProvider>
          </ThemeProvider>
        </I18nProvider>
      </QueryProvider>
    </JotaiProvider>
  );
}
